import React, { useState, useEffect } from "react";
import axios from "../../api/api";
import { Trash, RefreshCw, Plus, Image } from "lucide-react";
import { toast } from "react-toastify";

export default function AddGallery() {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [existingImagePreviews, setExistingImagePreviews] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get("/api/country")
      .then((resp) => setCountries(resp.data?.data || []))
      .catch(() => toast.error("Failed to load countries"));
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      axios
        .get(`/api/state/country/${selectedCountry}`)
        .then((resp) => setStates(resp.data?.data || []))
        .catch(() => toast.error("Failed to load states"));
    } else {
      setStates([]);
      setSelectedState("");
      resetImages();
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      setLoading(true);
      axios
        .get(`/api/state/${selectedState}`)
        .then((resp) => {
          const images = resp.data?.data?.images || [];
          setExistingImages(images);
          setExistingImagePreviews(images);
        })
        .catch(() => toast.error("Failed to load state images"))
        .finally(() => setLoading(false));
    } else {
      resetImages();
    }
    setNewImages([]);
    setNewImagePreviews([]);
  }, [selectedState]);

  const resetImages = () => {
    setExistingImages([]);
    setExistingImagePreviews([]);
    setNewImages([]);
    setNewImagePreviews([]);
  };

  const handleNewImageAdd = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    let loadedCount = 0;
    const newPreviews = [];
    const newAdded = [];

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        newPreviews.push(reader.result);
        newAdded.push(reader.result);
        loadedCount++;
        if (loadedCount === files.length) {
          setNewImagePreviews((prev) => [...prev, ...newPreviews]);
          setNewImages((prev) => [...prev, ...newAdded]);
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleNewImageDelete = (index) => {
    setNewImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExistingImageUpdate = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setExistingImagePreviews((prev) => {
        const updated = [...prev];
        updated[index] = reader.result;
        return updated;
      });
      setExistingImages((prev) => {
        const updated = [...prev];
        updated[index] = reader.result;
        return updated;
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleExistingImageDelete = (index) => {
    setExistingImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedState) {
      toast.error("Please select a state");
      return;
    }
    setSubmitting(true);
    try {
      const updatedImages = [...existingImages, ...newImages];
      await axios.put(`/api/state/${selectedState}`, { images: updatedImages });
      toast.success("Gallery updated successfully!");
      const resp = await axios.get(`/api/state/${selectedState}`);
      const images = resp.data?.data?.images || [];
      setExistingImages(images);
      setExistingImagePreviews(images);
      setNewImages([]);
      setNewImagePreviews([]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update gallery");
    } finally {
      setSubmitting(false);
    }
  };

  const totalImages = existingImagePreviews.length + newImagePreviews.length;

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24">
        <div className="xl:w-full min-h-[calc(100vh-152px)] pb-14">
          <div className="grid xl:grid-cols-12 gap-6">
            <div className="xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md">

                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 flex justify-between items-center">
                  <h4 className="font-semibold text-xl text-gray-800 dark:text-gray-200">
                    Manage Gallery
                  </h4>
                  {selectedState && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Total: {totalImages} image{totalImages !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                <form onSubmit={handleSubmit} className="p-6">

                  {/* Country */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select Country
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200"
                    >
                      <option value="">Select Country</option>
                      {countries.map((c) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* State */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select State
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      disabled={!selectedCountry}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-800 dark:text-gray-200 disabled:opacity-50"
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Image Sections */}
                  {selectedState && (
                    <div className="mt-6">

                      {/* ── Existing Images ── */}
                      <div className="mb-6">
                        <h5 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                          Existing Images
                        </h5>

                        {loading ? (
                          <div className="grid grid-cols-3 gap-4">
                            {[...Array(3)].map((_, i) => (
                              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
                            ))}
                          </div>
                        ) : existingImagePreviews.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                            <Image size={32} className="text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500 dark:text-gray-400">No images yet</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 gap-4">
                            {existingImagePreviews.map((preview, index) => (
                              <div
                                key={`existing-${index}`}
                                className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                              >
                                <img
                                  src={preview}
                                  alt={`existing-${index}`}
                                  className="w-full h-32 object-cover"
                                />
                                {/* Always-visible action buttons */}
                                <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                                  {/* Replace button */}
                                  <label className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 cursor-pointer hover:text-blue-700 transition">
                                    <RefreshCw size={13} />
                                    Replace
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => handleExistingImageUpdate(index, e)}
                                      className="hidden"
                                    />
                                  </label>
                                  {/* Delete button */}
                                  <button
                                    type="button"
                                    onClick={() => handleExistingImageDelete(index)}
                                    className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 transition"
                                  >
                                    <Trash size={13} />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* ── Add New Images ── */}
                      <div className="mb-6">
                        <h5 className="font-semibold text-lg text-gray-800 dark:text-gray-200 mb-3">
                          Add New Images
                        </h5>

                        <label className="flex items-center gap-2 w-fit px-4 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md cursor-pointer hover:border-primary-500 hover:text-primary-500 text-sm text-gray-600 dark:text-gray-400 transition mb-4">
                          <Plus size={16} />
                          Choose Images
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleNewImageAdd}
                            className="hidden"
                          />
                        </label>

                        {newImagePreviews.length > 0 && (
                          <div className="grid grid-cols-3 gap-4">
                            {newImagePreviews.map((preview, index) => (
                              <div
                                key={`new-${index}`}
                                className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700"
                              >
                                <div className="relative">
                                  <img
                                    src={preview}
                                    alt={`new-${index}`}
                                    className="w-full h-32 object-cover"
                                  />
                                  <span className="absolute top-1 left-1 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded">
                                    New
                                  </span>
                                </div>
                                {/* Always-visible delete */}
                                <div className="flex items-center justify-end px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                                  <button
                                    type="button"
                                    onClick={() => handleNewImageDelete(index)}
                                    className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:text-red-700 transition"
                                  >
                                    <Trash size={13} />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting || !selectedState}
                    className="px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50"
                  >
                    {submitting ? "Saving..." : "Update Gallery"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}